<script lang="ts">
  import Link from "$components/link.svelte";
  import Cart from "$components/cart/cart.svelte";
  import { t } from "$lib/services/i18n";
  import MenuButton from "./menu-button.svelte";
  import UspBanner from "$components/usp-banner.svelte";
  import { openModal } from "@thehood/svlib";
  import DevThemeToggle from "./dev-theme-toggle.svelte";
  import { LoginModal } from "./login";
  import { CustomerType } from "./_types";
  import { browser, dev } from "$app/environment";
  import type { NavItem } from "types/backend/models/admin/navbar.entity";
  import NavItemComponent from "./nav-item.svelte";
  import SubItemComponent from "./sub-items.svelte";
  import { Views, screenSize, navbarMode, INavMode } from "$lib/store/detect-view";
  import { isLoggedIn, logout, getUserInfo, isNavOpen, setNavIsOpen } from "$lib/store/auth-context";

  export let navData: NavItem[];

  let userType: string | undefined = undefined;

  $: items = [
    ...(navData as NavItem[]),
    ...(($isLoggedIn
      ? [
          { separator: true },
          {
            label: "Account information",
            url: "/account",
            mobileShow: true,
            tabletShow: true,
            desktopShow: false,
          },
          {
            url: "/account/events",
            label: "Events",
            mobileShow: true,
            tabletShow: true,
            desktopShow: false,
          },
          {
            url: "/account/transactions",
            label: "Purchase history",
            mobileShow: true,
            tabletShow: true,
            desktopShow: false,
          },

          ...(userType === CustomerType.CUSTOMER
            ? []
            : [
                { separator: true },

                {
                  label: "Ticket Listings",
                  url: "/account/products",
                  mobileShow: true,
                  tabletShow: false,
                  desktopShow: false,
                },
                {
                  label: "Ticket Sold",
                  url: "/account/sales",
                  mobileShow: true,
                  tabletShow: true,
                  desktopShow: false,
                },
                { separator: true },
                {
                  label: "Payouts",
                  url: "/account/payouts",
                  mobileShow: true,
                  tabletShow: false,
                  desktopShow: false,
                },
                {
                  label: "Payout Information",
                  url: "/account/payout",
                  mobileShow: true,
                  tabletShow: false,
                  desktopShow: false,
                },
              ]),
        ]
      : []) as NavItem[]),
  ];

  function openLogin() {
    // close menu
    setNavIsOpen(false);

    // open modal
    openModal(LoginModal, {
      conditionalOnCloseCallback: () => setNavIsOpen(true),
    });
  }

  function closeMenu(ev: Event) {
    ev.stopImmediatePropagation();
    if ($screenSize === Views.DESKTOP) {
      return;
    }
    setNavIsOpen(false);
  }

  function toggleMenu(ev: Event) {
    ev.stopImmediatePropagation();
    setNavIsOpen(!$isNavOpen);
  }

  // function toggleNew(ev: Event) {
  //   ev.stopImmediatePropagation();
  //   console.log("Mystery button");
  // }

  $: if ($screenSize === Views.DESKTOP) {
    setNavIsOpen(false);
  }

  // Scroll logic for nav background
  let y = 0;
  function scrollY(yPos: number) {
    if (!browser) {
      return;
    }
    const r = document.querySelector(":root") as HTMLElement;
    if (yPos > 0) {
      r.style.setProperty("--nav-trans", "1");
      return;
    }

    if (yPos === 0) {
      yPos = 0.1;
    }

    let transparency = 0.4 + yPos / 90;
    if (transparency > 1) {
      transparency = 1;
    }

    r.style.setProperty("--nav-trans", `${transparency}`);
  }

  $: scrollY(y);

  function generateButtons(userStatus: boolean) {
    return [
      ...(userStatus
        ? [
            {
              label: "Sign out",
              callback: () => {
                logout();
              },
              backgroundColor: "#3649ce",
              color: "white",
              icon: {
                path: "https://cdn.builder.io/api/v1/image/assets/TEMP/c11990aa24ddc289b90a9b0d875b9bac3adb275b11cb99df9ae4425dc3d873a8?apiKey=e2bec90a26784722801b16c70d1f86f3&",
                imgAlt: "Sign out",
              },
            },
          ]
        : [
            {
              label: "Create an account or sign in",
              callback: openLogin,
              backgroundColor: "#100f21",
              color: "white",
              icon: {
                path: "https://cdn.builder.io/api/v1/image/assets/TEMP/c11990aa24ddc289b90a9b0d875b9bac3adb275b11cb99df9ae4425dc3d873a8?apiKey=e2bec90a26784722801b16c70d1f86f3&",
                imgAlt: "Sign in",
              },
            },
          ]),
      // {
      //   label: "Sell your tickets",
      //   url: "/sell-tickets",
      //   backgroundColor: "#ca0090",
      //   color: "white",
      //   icon: {
      //     path: "https://cdn.builder.io/api/v1/image/assets/TEMP/c45883f4d372c3811a3353bc126574ef333e22a9eb84eeb155d2682d0436d901?apiKey=e2bec90a26784722801b16c70d1f86f3&",
      //     imgAlt: "Sell your tickets",
      //   },
      // },
    ];
  }

  async function getUserType(loggedIn: boolean) {
    if (loggedIn) {
      try {
        const user = await getUserInfo();
        userType = user.userType;
      } catch (err) {
        console.error(err);
        userType = undefined;
      }
    }
  }

  $: getUserType($isLoggedIn);
  $: buttons = generateButtons($isLoggedIn);
</script>

<svelte:window bind:scrollY={y} />

<div class="sticky">
  <UspBanner />
  {#if $navbarMode === INavMode.FULL}
    <div class:content-grid={$screenSize === Views.DESKTOP}>
      <nav class="navbar" class:dev class:open={$isNavOpen}>
        <div class="relative-point-for-svg">
          <div class="nav-grid">
            <a class="navbar-brand grid-a" href="/">
              <span class="icon" />
              <span class="brand-text">Football TicketPad</span>
            </a>

            <div class="navbar-collapse grid-b" class:open={$isNavOpen}>
              <div class="close-button">
                <button class="close-button" on:click={closeMenu} />
              </div>
              <ul class="navbar-nav">
                {#each items as item}
                  {@const { subItems } = item}

                  {#if subItems}
                    <SubItemComponent menuOpen={$isNavOpen} {closeMenu} data={item} />
                  {:else if item.separator}
                    {#if $screenSize !== Views.DESKTOP}
                      <li class="separator"></li>
                    {/if}
                  {:else}
                    <NavItemComponent {closeMenu} data={item} />
                  {/if}
                {/each}
                {#if $screenSize !== Views.DESKTOP}
                  {#each buttons as button}
                    <MenuButton {button} />
                  {/each}
                {/if}
              </ul>
            </div>
            <div class="cart-toggler grid-c">
              {#if $screenSize === Views.DESKTOP}
                <div class="auth-container">
                  {#if $isLoggedIn}
                    {#if userType !== CustomerType.BROKER}
                      <Link href="/account/events"><span class="icon-profile" /></Link>
                    {:else}
                      <Link href="/account/products"><span class="icon-profile" /></Link>
                    {/if}
                  {:else}
                    <button class="auth-btn" type="button" on:click={openLogin}>
                      <span class="icon-profile" />
                      <span class="text">{$t("Sign In")}</span>
                    </button>
                  {/if}
                </div>
              {/if}
              <Cart />
              <button class="navbar-toggler-btn" on:click={toggleMenu}>
                <span class="visually-hidden">Toggle navigation</span>
                <span class="icon-burger" />
              </button>
              <!--
                <button class="desk-only navbar-ticket-btn" on:click={toggleNew}>
                  <span class="visually-hidden">Toggle Something Else</span>
                  <span class="icon-tickets" />
                </button> -->
              {#if dev}
                <DevThemeToggle />
              {/if}
            </div>
          </div>
        </div>
      </nav>
      {#if $isNavOpen}
        <div class="overlay" tabindex="0" role="button" on:keydown={toggleMenu} on:click={toggleMenu} />
      {/if}
    </div>
  {/if}
</div>

<style lang="scss">
  @use "@styles/breakpoint-mixins" as *;
  $logoWidth: 170px;
  $logoWidthMob: 130px;
  $navBgMob: #fdfafa;
  $borderColour: #d4d4d4;
  $navbarHeightMob: 54px;
  $navbarHeight: 82px;

  .sticky {
    position: fixed;
    left: -6px;
    top: 0;
    /* top: -4px; */
    right: 0;
    z-index: var(--z2);
    background: #fff;
    @include desktop {
      /* top: -9px; */
    }
  }

  .relative-point-for-svg {
    position: relative;
    width: 100%;
  }

  .content-grid {
    background: inherit;
  }

  .overlay {
    position: fixed;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background: #000000cc;
    cursor: pointer;
    z-index: var(--z1);
  }

  .navbar {
    display: flex;
    height: $navbarHeightMob;
    padding: 0 0;
    color: #000917;
    width: 100%;
    @include desktop {
      height: $navbarHeight;
      width: 100%;
      clip-path: unset;
      border-bottom-left-radius: unset;
      border-bottom-right-radius: unset;
    }

    &.open {
      margin-top: -46px; // this is for the USP block
    }
  }

  .separator {
    margin: 0;
    padding: 0;
    height: 1px;
    background: #010ebc;
    margin-bottom: 1.125rem;
  }

  .nav-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr auto;
    gap: 0;
    padding: 0 20px 0 30px;
    grid-template-areas:
      "grid-a grid-c"
      "grid-b grid-b";

    width: 100%;
    z-index: var(--z1);
    height: $navbarHeightMob;

    @include desktop {
      padding: unset;
      grid-template-columns: 220px 1fr auto;
      grid-template-areas: "grid-a grid-b grid-c";
      height: $navbarHeight;
      background: unset;
      padding: 0 50px;
    }
    @include desktop-xl {
      padding: unset;
    }
  }

  .grid-a {
    grid-area: grid-a;
  }

  .grid-c {
    grid-area: grid-c;
  }

  .grid-b {
    grid-area: grid-b;
  }

  // Logo
  .navbar-brand {
    cursor: pointer;
    display: flex;
    align-items: center;
    max-height: $navbarHeightMob;
    z-index: var(--z2);
    margin: 0;
    padding: 0;
    @include desktop {
      max-height: $navbarHeight;
    }

    .icon {
      width: $logoWidthMob;
      background: url("@styles/assets/logo.svg") no-repeat;
      background-size: cover;
      height: 22px;

      @include desktop {
        width: $logoWidth;
        height: 28px;
        background-size: cover;
      }

      .light & {
        background: url("@styles/assets/logo.svg") no-repeat;
      }

      .dark & {
        // @TODO-LOGO text is White
        background: url("@styles/assets/logo-dark.svg") no-repeat;
      }
    }

    .brand-text {
      display: none;
    }
  }

  // Menu
  .navbar-collapse {
    width: 100%;
    justify-content: space-between;
    height: 100%;
    padding: 58px 35px 0px;
    position: absolute;
    top: -110vh;
    left: 0;
    right: 0;
    background: #fff;
    z-index: var(--z2);
    height: auto;
    max-height: calc(100vh - 42px);
    overflow-x: hidden;
    overflow-y: scroll;

    transition:
      top 0.3s,
      left 0.3s;

    .close-button {
      display: flex;
      justify-content: flex-end;
      margin: -1em -0.5em 1em 0;
      @include desktop {
        display: none;
      }
      button {
        width: 1em;
        background: url("@styles/assets/close.svg") no-repeat;
        background-size: contain;
        height: 1em;
        border: none;
      }
    }

    @include desktop {
      transition:
        top 0.3s,
        left 0.3s;
      top: 0;
      display: flex;
      justify-content: center;
      position: relative;
      flex: 1 0;
      align-items: center;
      border-radius: 0;
      padding: 0;
      column-gap: 10px;
      background: 0;
      border-bottom: none;
      clip-path: none;
      overflow-x: unset;
      overflow-y: unset;
    }

    &.open {
      top: 0px;
    }

    // UL Main Menu
    .navbar-nav {
      list-style: none;
      padding-left: 0;
      display: grid;
      grid-template-columns: 1fr;
      z-index: var(--z2);
      top: 0;
      left: 0;
      right: 0;

      @include desktop {
        display: flex;
        column-gap: 20px;
      }
    }
  }

  .cart-toggler {
    display: flex;
    justify-content: right;
    align-items: center;
    gap: 20px;

    .auth-container {
      display: flex;

      & :global(> a) {
        height: 30px;
      }

      & * {
        user-select: none;
      }

      .icon-profile {
        display: inline-block;
        width: 24px;
        height: 24px;
        background: url(./assets/profile.svg) no-repeat;
        background-size: cover;

        @include desktop {
          width: 32px;
          height: 28px;
          background: url(./assets/profile.svg) no-repeat;
          background-size: contain;
        }

        .light & {
          background: url(./assets/profile-dark.svg) no-repeat !important;
        }

        .dark & {
          background: url(./assets/profile.svg) no-repeat !important;
        }
      }

      .auth-btn {
        border: 0;
        background: 0;
        display: flex;
        align-items: center;
        gap: 10px;
        margin: 0;
        padding: 0;

        .text {
          font-size: 25px;
          font-weight: 600;
          line-height: 100%;
          color: black;

          @include desktop {
            position: unset;
            width: auto;
            height: auto;
            overflow: unset;
            font-size: 16px;
            white-space: nowrap;
          }
        }
      }
    }

    // Hamburger
    .navbar-toggler-btn {
      border: 0;
      background: 0;
      width: 32px;
      padding: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;

      @include desktop {
        display: none;
      }

      .icon-burger {
        display: block;
        width: 24px;
        height: 24px;
        background: url("./assets/burger.svg") no-repeat;
        background-size: cover;
      }
    }

    // sell ticket, desktop only
    /* .navbar-ticket-btn {
        display: flex;
        width: 44px;
        height: 44px;
        justify-content: center;
        align-items: center;
        gap: 5px;
        border-radius: 95px;
        background: var(--tp-pink);
        padding: unset;

        .icon-tickets {
          display: block;
          width: 16px;
          height: 16px;
          background: url("./assets/tickets.svg") no-repeat;
          background-size: cover;
        }
      } */
  }
</style>
